import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-use";
import { RouteFactory } from "@app/routePaths";
import { useHookUpdatePulseUrl } from "@features/pulse/hooks/useHookUpdatePulseUrl";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { CARD_SIZE } from "@features/pulse/utils/constants";
import { getActiveArticleId } from "@features/pulse/utils/helpers";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import useSendChartbeatViritualPageData from "@hooks/useSendChartbeatViritualPageData";
import { LIVE_DOMAIN } from "@util/constant";
import posthog from "posthog-js";

type useNavigateCarouselProps = {
  /** List of articles to navigate through in carousel */
  articleList: SanitizedPulseData[];
};

/**
 * This hook manages the navigation of a carousel of articles.
 * It provides functionality to navigate left/right, set active articles, and handle browser navigation.
 * It also updates the URL and sends tracking data for analytics.
 */
export const useNavigateCarousel = ({
  articleList,
}: useNavigateCarouselProps) => {
  const location = useLocation();
  const [offset, setOffset] = useState(0);
  const [activeArticle, setActiveArticle] = useState(1);
  const [activeArticleId, setActiveArticleId] = useState<string>("");

  const { updatePulseUrl } = useHookUpdatePulseUrl();
  const { sendTrackingData } = useSendPulseGaData();
  const { sendChartbeatData } = useSendChartbeatViritualPageData();

  /** Handle for updating of url and tracking */
  const handleUpdatePulseUrlAndTracking = useCallback(
    (
      articleIndex: number,
      articleList: SanitizedPulseData[],
      isNotPopstate: boolean = true
    ) => {
      const activeList = articleList?.[articleIndex];
      const activeId = activeList ? activeList.id : "finished";
      const virtualUrl = RouteFactory.pulseSlide(activeId);

      if (isNotPopstate) {
        updatePulseUrl(activeId);
        setActiveArticleId(activeId);
      }

      const pagination = `${articleIndex + 1}/${articleList.length}`;
      const title = activeList ? activeList.title : "pulse outro";

      sendTrackingData(
        title,
        virtualUrl,
        activeId,
        articleList,
        pagination.toString()
      );

      posthog.capture("$pageview", {
        $pathname: virtualUrl,
      });

      sendChartbeatData({
        url: `${LIVE_DOMAIN}${virtualUrl}`,
        title,
        sections: activeList?.sections || "",
      });
    },
    [updatePulseUrl, sendTrackingData, sendChartbeatData]
  );

  /** Handle Navigate to next card */
  const handleNavigateRightCard = useCallback(() => {
    setActiveArticle((prev) => prev + 1);

    handleUpdatePulseUrlAndTracking(activeArticle, articleList);

    if (activeArticle > articleList.length) return;
    setOffset((prev) => (prev -= CARD_SIZE));
  }, [activeArticle, articleList, handleUpdatePulseUrlAndTracking]);

  /** Handle Navigate to prev card */
  const handleNavigateLeftCard = useCallback(() => {
    if (activeArticle - 1 === 0) return;

    setOffset((prev) => (prev += CARD_SIZE));

    setActiveArticle((prev) => prev - 1);

    handleUpdatePulseUrlAndTracking(activeArticle - 2, articleList);
  }, [activeArticle, articleList, handleUpdatePulseUrlAndTracking]);

  /** Handle Set Active Article id for control mobile */
  const handleSetActiveArticle = (index: number) => {
    if (index < 1 || index > articleList.length + 1) return;
    setActiveArticle(index + 1);

    if (activeArticle === articleList.length + 1) return;
    handleUpdatePulseUrlAndTracking(index, articleList);
  };

  const handleBrowserNavigation = useCallback(() => {
    const activeId = getActiveArticleId(window.location.pathname);
    const activeIndex = articleList.findIndex(
      (article) => article.id === activeId
    );

    if (activeIndex === -1) return;
    setActiveArticle(activeIndex + 1);
    setOffset(-CARD_SIZE * activeIndex);
    handleUpdatePulseUrlAndTracking(activeIndex, articleList, false);
  }, [articleList, handleUpdatePulseUrlAndTracking]);

  /**
   * Handle Popstate event to update active article
   * This is to handle browser back/forward navigation
   */
  useEffect(() => {
    if (location.trigger !== "popstate") return;

    handleBrowserNavigation();
  }, [location, handleBrowserNavigation]);

  return {
    offset,
    setOffset,
    activeArticle,
    activeArticleId,
    setActiveArticle,
    handleNavigateRightCard,
    handleNavigateLeftCard,
    handleSetActiveArticle,
  };
};
